import { useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import type { MapRef, ViewStateChangeEvent } from 'react-map-gl/mapbox'
import type { MapCameraPosition } from 'tripPlanner/api/types'
import { isValidCoordinate, type MapCoordinate, type MapMarkerComponent } from 'tripPlanner/utils/mapMarkerTransforms'
import {
  DEFAULT_VIEW_STATE,
  DEFAULT_PADDING,
  EMPTY_FIT_BOUNDS_COORDINATES,
  FALLBACK_MAX_ZOOM,
  flattenCoordinates,
  collectBoundsFromCoordinates,
} from './chatMapConstants'
import type { MapPathComponent, MapRegionComponent } from './chatMapConstants'
import {
  getMapPitchCamera,
  type MapPitchMode,
} from 'tripPlanner/components/ChatMap/map3DConfig'

/**
 * Mapbox's movestart/moveend events carry `originalEvent` when triggered by a
 * user gesture (drag, scroll, pinch) and omit it for programmatic moves
 * (flyTo, fitBounds, jumpTo). react-map-gl's ViewStateChangeEvent type doesn't
 * surface this property, but it exists at runtime on the underlying MapboxEvent.
 *
 * Extends ViewStateChangeEvent to match the mapbox-gl type definition:
 * `"movestart": { originalEvent?: MouseEvent | WheelEvent | TouchEvent }`
 * (see MapEvents interface in node_modules/mapbox-gl/dist/mapbox-gl.d.ts)
 */
export interface MapMoveEvent extends ViewStateChangeEvent {
  originalEvent?: MouseEvent | TouchEvent | WheelEvent
}

type MapInstance = ReturnType<MapRef['getMap']>
type BoundsTuple = NonNullable<ReturnType<typeof collectBoundsFromCoordinates>>

function applyBoundsCamera(
  map: MapInstance,
  bounds: BoundsTuple,
  shouldSkipAnimation: boolean,
  mapPitchMode: MapPitchMode,
  stopRotation?: () => void,
) {
  stopRotation?.()
  map.stop()
  const pitchCamera = getMapPitchCamera(mapPitchMode)
  if (shouldSkipAnimation) {
    const camera = map.cameraForBounds(bounds, { padding: DEFAULT_PADDING, maxZoom: FALLBACK_MAX_ZOOM })
    if (camera) {
      map.jumpTo({ ...camera, ...pitchCamera })
    }
    return
  }

  map.fitBounds(bounds, {
    padding: DEFAULT_PADDING,
    maxZoom: FALLBACK_MAX_ZOOM,
    ...pitchCamera,
    duration: 1200,
    essential: true,
  })
}

interface UseMapCameraParams {
  mapRef: React.RefObject<MapRef | null>
  isMapLoaded: boolean
  shouldSkipAnimation: boolean
  chatKey?: string
  markers: Array<MapMarkerComponent>
  paths: Array<MapPathComponent>
  regions: Array<MapRegionComponent>
  shouldResetToGlobe?: boolean
  shouldSpin: boolean
  initialCamera?: MapCameraPosition
  aiCamera?: MapCameraPosition
  aiCameraKey: number
  fitBoundsCoordinates?: Array<MapCoordinate>
  fitBoundsKey?: number
  onFitBoundsHandled?: () => void
  stopRotation: () => void
  onAiCameraClear?: () => void
  mapPitchMode: MapPitchMode
}

/**
 * Manages all programmatic map camera movements:
 *
 *   1. Globe reset — when all markers are removed and no AI camera is active.
 *   2. Saved viewport restore — when switching back to a previously visited
 *      chat, the last-seen center/zoom is restored instead of fitBounds.
 *   3. Initial fitBounds — one-time fit when a chat loads with markers but
 *      no saved viewport and no AI camera.
 *   4. AI-driven flyTo — explicit coordinates from a tool call.
 *
 * User interaction detection uses Mapbox's native `originalEvent` property
 * on movement events: programmatic moves (flyTo, fitBounds, jumpTo) have
 * `originalEvent === undefined`, while user gestures (drag, scroll, pinch)
 * carry the originating DOM event. This is the SDK-recommended approach
 * (see https://github.com/mapbox/mapbox-gl-js/issues/9700).
 *
 * Each programmatic animation calls `map.stop()` first to cleanly cancel
 * any in-progress animation, avoiding interrupted-moveend race conditions.
 */
export function useMapCamera({
  mapRef,
  isMapLoaded,
  shouldSkipAnimation,
  chatKey,
  markers,
  paths,
  regions,
  shouldResetToGlobe,
  shouldSpin,
  initialCamera,
  aiCamera,
  aiCameraKey,
  fitBoundsCoordinates = EMPTY_FIT_BOUNDS_COORDINATES,
  fitBoundsKey = 0,
  onFitBoundsHandled,
  stopRotation,
  onAiCameraClear,
  mapPitchMode,
}: UseMapCameraParams) {
  const mapPitchModeRef = useRef(mapPitchMode)

  useEffect(() => {
    mapPitchModeRef.current = mapPitchMode
  }, [mapPitchMode])

  const handleMoveStart = useCallback((e: MapMoveEvent) => {
    if (!e.originalEvent) return
    stopRotation()
    onAiCameraClear?.()
  }, [stopRotation, onAiCameraClear])

  // ── Globe reset ──────────────────────────────────────────────────────
  // When all markers are removed and no camera is active, return to globe.
  useEffect(() => {
    if (!shouldResetToGlobe || shouldSpin || aiCamera || initialCamera) return
    const map = mapRef.current?.getMap()
    if (!map) return
    map.stop()
    if (shouldSkipAnimation) {
      map.jumpTo({
        center: [DEFAULT_VIEW_STATE.longitude, DEFAULT_VIEW_STATE.latitude],
        zoom: DEFAULT_VIEW_STATE.zoom,
        pitch: DEFAULT_VIEW_STATE.pitch,
        bearing: DEFAULT_VIEW_STATE.bearing,
      })
    } else {
      map.flyTo({
        center: [DEFAULT_VIEW_STATE.longitude, DEFAULT_VIEW_STATE.latitude],
        zoom: DEFAULT_VIEW_STATE.zoom,
        pitch: DEFAULT_VIEW_STATE.pitch,
        bearing: DEFAULT_VIEW_STATE.bearing,
        duration: 2000,
        essential: true,
      })
    }
  }, [shouldResetToGlobe, shouldSpin, shouldSkipAnimation, aiCamera, initialCamera, mapRef])

  // ── Viewport cache (per-chat) ────────────────────────────────────────
  // Snapshots the map's center/zoom on chat switch so the viewport can be
  // restored when the user switches back.  Uses useLayoutEffect so it
  // fires BEFORE useMapGlobeRotation's useEffects can jumpTo the default
  // globe position — otherwise switching A → empty chat → A would
  // overwrite Chat A's cached viewport with the globe.
  const viewportCacheRef = useRef(new Map<string, MapCameraPosition>())
  const prevChatKeyRef = useRef(chatKey)
  const hasInitialFitRef = useRef(false)

  useLayoutEffect(() => {
    if (!prevChatKeyRef.current || prevChatKeyRef.current === chatKey) return
    const map = mapRef.current?.getMap()
    if (map) {
      const center = map.getCenter()
      viewportCacheRef.current.set(prevChatKeyRef.current, {
        lat: center.lat,
        lng: center.lng,
        zoom: map.getZoom(),
      })
    }
    prevChatKeyRef.current = chatKey
    hasInitialFitRef.current = false
  }, [chatKey, mapRef])

  // ── Initial-load: restored viewport OR fitBounds ─────────────────────
  useEffect(() => {
    if (hasInitialFitRef.current || !isMapLoaded) return
    if (aiCamera) {
      hasInitialFitRef.current = true
      return
    }

    if (initialCamera) {
      hasInitialFitRef.current = true
      return
    }

    // Restore saved viewport from a previous visit to this chat.
    if (chatKey) {
      const saved = viewportCacheRef.current.get(chatKey)
      if (saved) {
        hasInitialFitRef.current = true
        const map = mapRef.current?.getMap()
        if (!map) return
        map.stop()
        const pitchMode = mapPitchModeRef.current
        if (shouldSkipAnimation) {
          map.jumpTo({ center: [saved.lng, saved.lat], zoom: saved.zoom, ...getMapPitchCamera(pitchMode) })
        } else {
          map.flyTo({
            center: [saved.lng, saved.lat],
            zoom: saved.zoom,
            ...getMapPitchCamera(pitchMode),
            duration: 800,
            essential: true,
          })
        }
        return
      }
    }

    // Fall through to fitBounds for first-time visits.
    const validMarkers = markers.filter(isValidCoordinate)
    const nonOriginMarkers = validMarkers.filter(marker => !marker.isOriginStop)
    const markersForBounds = nonOriginMarkers.length > 0 ? nonOriginMarkers : validMarkers

    const allCoords = flattenCoordinates({
      markers: markersForBounds,
      paths,
      regions,
    })
    if (allCoords.length === 0) return
    hasInitialFitRef.current = true
    const map = mapRef.current?.getMap()
    if (!map) return
    const bounds = collectBoundsFromCoordinates(allCoords)
    if (!bounds) return
    applyBoundsCamera(map, bounds, shouldSkipAnimation, mapPitchModeRef.current)
  }, [isMapLoaded, markers, aiCamera, initialCamera, shouldSkipAnimation, chatKey, paths, regions, mapRef])

  // ── AI-driven camera (aiCamera) ───────────────────────────────────
  useEffect(() => {
    // aiCameraKey starts at 0 — the falsy check prevents firing on mount.
    // Each moveMapTo() call increments the key, triggering this effect.
    if (!aiCamera || !aiCameraKey || !isMapLoaded) return
    const map = mapRef.current?.getMap()
    if (!map) return
    stopRotation()
    map.stop()
    const pitchMode = mapPitchModeRef.current
    if (shouldSkipAnimation) {
      map.jumpTo({
        center: [aiCamera.lng, aiCamera.lat],
        zoom: aiCamera.zoom,
        ...getMapPitchCamera(pitchMode),
      })
    } else {
      map.flyTo({
        center: [aiCamera.lng, aiCamera.lat],
        zoom: aiCamera.zoom,
        ...getMapPitchCamera(pitchMode),
        essential: true,
      })
    }
  }, [aiCameraKey, aiCamera, isMapLoaded, shouldSkipAnimation, stopRotation, mapRef])

  // ── Manual fit-bounds camera ───────────────────────────────────────
  useEffect(() => {
    if (!fitBoundsKey || !isMapLoaded || fitBoundsCoordinates.length === 0) return
    const map = mapRef.current?.getMap()
    if (!map) return
    const bounds = collectBoundsFromCoordinates(fitBoundsCoordinates)
    if (!bounds) return
    applyBoundsCamera(map, bounds, shouldSkipAnimation, mapPitchModeRef.current, stopRotation)
    onFitBoundsHandled?.()
  }, [fitBoundsKey, fitBoundsCoordinates, isMapLoaded, shouldSkipAnimation, onFitBoundsHandled, stopRotation, mapRef])

  return { handleMoveStart }
}
